import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';

const TextEvaluator = ({ onSubmit, loading, error, result }) => {
  const [textToCheck, setTextToCheck] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      textToCheck
    });
  };

  return (
    <Card
      variant="outlined"
      sx={{
        width: '100%',
        maxWidth: 800,
        mx: 'auto',
        bgcolor: 'background.paper',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter text to evaluate"
            value={textToCheck}
            onChange={(e) => setTextToCheck(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#fff',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                  borderWidth: '1.5px',
                },
                '&:hover fieldset': {
                  borderColor: '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1.5px',
                },
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <ShieldIcon />}
              sx={{
                height: 48,
                borderRadius: 6,
                textTransform: 'none',
                fontSize: '1rem',
                px: 4,
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              {loading ? 'Checking...' : 'Check Text'}
            </Button>
          </Box>
        </form>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mt: 2, borderRadius: 2 }}
            icon={<ErrorOutlineIcon />}
          >
            {error}
          </Alert>
        )}

        {result && (
          <Paper 
            elevation={0} 
            sx={{ 
              mt: 3, 
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Status Header */}
            <Box
              sx={{
                p: 2,
                bgcolor: result.is_clean ? 'success.main' : 'error.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              {result.is_clean ? 
                <CheckCircleOutlineIcon sx={{ color: 'white' }} /> :
                <WarningAmberIcon sx={{ color: 'white' }} />
              }
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
                Content is {result.is_clean ? 'safe' : 'not safe'}
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              <Stack spacing={2}>
                {/* Policy Violations */}
                {result.policy_violations?.length > 0 && (
                  <Box>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <ErrorOutlineIcon color="error" />
                      Policy Violations Detected
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {result.policy_violations.map((violation, index) => (
                        <Chip
                          key={index}
                          label={violation}
                          color="error"
                          variant="outlined"
                          size="medium"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Zero Tolerance Terms */}
                {result.matched_terms?.length > 0 && (
                  <Box>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <BlockIcon color="error" />
                      Zero Tolerance Terms Found
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {result.matched_terms.map((term, index) => (
                        <Chip
                          key={index}
                          label={term}
                          color="error"
                          variant="outlined"
                          size="medium"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            </Box>
          </Paper>
        )}
      </CardContent>
    </Card>
  );
};

export default TextEvaluator; 